import { some } from 'lodash';
import { getType } from 'typesafe-actions';
import { AppState } from '../../app-state';
import { AuthActions } from '../../auth/actions/auth.types';
import { KeyValueMap } from '../../common/types/KeyValueMap.type';
import {
  closeDocument,
  fetchAndSetSourceDocument,
  setSourceDocument,
} from '../../documents/actions/source-document.actions';
import { SourceDocumentActions } from '../../documents/actions/source-document.types';
import { PredictionsActions } from '../../predictions/actions/predictions.types';
import {
  fetchAnnotationPages,
  fetchAnnotationPagesError,
} from '../actions/annotation-pages.actions';
import { AnnotationPagesActions } from '../actions/annotation-pages.types';
import {
  addToChainNesting,
  appendAnnotations,
  cancelChainNesting,
  copyAnnotations,
  createAnnotation,
  deleteAnnotation,
  deleteAnnotations,
  fetchAnnotations,
  fetchAnnotationsError,
  replaceAnnotations,
  saveAnnotations,
  saveAnnotationsError,
  saveAnnotationsSuccess,
  selectAnnotationsOfType,
  setAnnotationSaveEvent,
  setDragTargetId,
  setDragTargetState,
  setLastManualAnnotation,
  setSelectedAnnotations,
  splitAnnotation,
  toggleAnnotationSelection,
  updateAnnotationTypes,
  updateAnnotationValidations,
  updateAnnotationValues,
} from '../actions/annotations.actions';
import { AnnotationsActions } from '../actions/annotations.types';
import { AnnotationSaveEvent } from '../models/annotation-save-event.model';
import {
  Annotation,
  AnnotationMetadata,
  AnnotationSegment,
} from '../models/annotation.model';
import uuid from 'uuid/v4';
import { AnnotationSchemaActions } from '../actions/annotation-schemas.types';
import { Map } from 'immutable';
import {
  selectAnnotations,
  selectAnnotationsArray,
} from '../selectors/annotation.selectors';
import { DragTargetState } from '../models/drag-target-state.model';
import { selectUsername } from '../../accounts/selectors/users.selectors';

export type AnnotationsState = {
  sourceDocumentId: number | null;
  metadata: Map<string, AnnotationMetadata>;
  values: Map<string, string>;
  types: Map<string, string>;
  validations: Map<string, boolean | undefined>;
  ids: string[];
  error: string | null;
  isSaving: boolean;
  saveEvent: AnnotationSaveEvent | null;
  saveError: string | null;
  lastSaved: number | null;
  loading: boolean;
  lastManualAnnotationId: string | null;
  selectedAnnotations: string[];
  chainNestingOrder: string[];
  dragTargetId: string | null;
  dragTargetState: DragTargetState;
  clipboard: Map<string, Annotation>;
};

export const defaultAnnotationsState = {
  sourceDocumentId: null,
  ids: [],
  metadata: Map<string, AnnotationMetadata>(),
  values: Map<string, string>(),
  types: Map<string, string>(),
  validations: Map<string, boolean | undefined>(),
  error: null,
  isSaving: false,
  saveEvent: null,
  lastSaved: null,
  saveError: null,
  loading: false,
  lastManualAnnotationId: null,
  selectedAnnotations: [],
  chainNestingOrder: [],
  dragTargetId: null,
  dragTargetState: DragTargetState.None,
  clipboard: Map<string, Annotation>(),
};

export function annotationsReducer(
  state: AnnotationsState = defaultAnnotationsState,
  action:
    | AnnotationsActions
    | AnnotationPagesActions
    | AuthActions
    | SourceDocumentActions
    | PredictionsActions
    | AnnotationSchemaActions,
  appState: AppState,
): AnnotationsState {
  switch (action.type) {
    case getType(fetchAnnotations): {
      const newSourceId = action.payload.sourceDocumentId;
      const sourceChanged = newSourceId !== state.sourceDocumentId;
      return {
        ...state,
        sourceDocumentId: newSourceId,
        ids: [],
        metadata: Map<string, AnnotationMetadata>(),
        values: Map<string, string>(),
        types: Map<string, string>(),
        validations: Map<string, boolean | undefined>(),
        isSaving: false,
        saveEvent: null,
        lastSaved: sourceChanged ? null : state.lastSaved,
        saveError: null,
        loading: true,
      };
    }

    case getType(fetchAnnotationsError): {
      return {
        ...state,
        ids: [],
        metadata: Map<string, AnnotationMetadata>(),
        values: Map<string, string>(),
        types: Map<string, string>(),
        validations: Map<string, boolean | undefined>(),
        error: action.payload.error,
        isSaving: false,
        saveEvent: null,
        lastSaved: null,
        saveError: null,
        loading: false,
      };
    }

    case getType(appendAnnotations): {
      const { annotations, sourceDocumentId } = action.payload;
      const newAnnotations = Array.from(annotations.entries());
      const oldAnnotations = Array.from(selectAnnotations(appState).entries());
      const allAnnotations = Map(oldAnnotations.concat(newAnnotations));

      const values = allAnnotations.mapEntries(([id, a]) => [id, a.value]);
      const types = allAnnotations.mapEntries(([id, a]) => [id, a.type]);
      const ids = Array.from(allAnnotations.keys());

      return {
        ...state,
        sourceDocumentId,
        loading: false,
        metadata: allAnnotations,
        values,
        types,
        ids,
      };
    }

    case getType(replaceAnnotations): {
      const { annotations, sourceDocumentId } = action.payload;

      const values = annotations.mapEntries(([id, a]) => [id, a.value]);
      const types = annotations.mapEntries(([id, a]) => [id, a.type]);
      const ids = Array.from(annotations.keys());

      return {
        ...state,
        sourceDocumentId,
        metadata: annotations,
        values,
        types,
        ids,
      };
    }

    case getType(copyAnnotations): {
      const { annotationIds } = action.payload;
      const annotations = selectAnnotationsArray(appState);
      const clipboard = annotations.filter(a =>
        some(annotationIds, i => a.id === i),
      );
      return {
        ...state,
        clipboard: KeyValueMap.usingKey(clipboard, 'id'),
      };
    }

    case getType(createAnnotation): {
      const { annotation, sourceDocumentId } = action.payload;
      const id = annotation.id;

      return {
        ...state,
        sourceDocumentId,
        metadata: state.metadata.set(id, annotation),
        values: state.values.set(id, annotation.value),
        types: state.types.set(id, annotation.type),
        ids: state.ids.concat([id]),
      };
    }

    case getType(setLastManualAnnotation): {
      const id = action.payload.id;
      return {
        ...state,
        lastManualAnnotationId: id,
      };
    }

    case getType(updateAnnotationValues): {
      const ids = Array.from(action.payload.keys());
      const metadata = updateAnnotatedBy(appState, state)(ids);
      return {
        ...state,
        values: state.values.merge(action.payload),
        metadata,
      };
    }

    case getType(updateAnnotationTypes): {
      const ids = Array.from(action.payload.keys());
      const metadata = updateAnnotatedBy(appState, state)(ids);
      return {
        ...state,
        types: state.types.merge(action.payload),
        metadata,
      };
    }

    case getType(updateAnnotationValidations): {
      return {
        ...state,
        validations: state.validations.merge(action.payload),
      };
    }

    case getType(deleteAnnotation): {
      const { annotationId } = action.payload;
      return {
        ...state,
        ids: state.ids.filter(i => i !== annotationId),
        metadata: state.metadata.delete(annotationId),
        values: state.values.delete(annotationId),
        types: state.types.delete(annotationId),
        validations: state.validations.delete(annotationId),
      };
    }

    case getType(deleteAnnotations): {
      const { annotationIds } = action.payload;
      return {
        ...state,
        ids: state.ids.filter(id => !some(annotationIds, i => id === i)),
        metadata: state.metadata.deleteAll(annotationIds),
        values: state.values.deleteAll(annotationIds),
        types: state.types.deleteAll(annotationIds),
        validations: state.validations.deleteAll(annotationIds),
      };
    }

    case getType(setSelectedAnnotations): {
      const annotationIds = action.payload.annotationIds;
      return {
        ...state,
        selectedAnnotations: annotationIds,
      };
    }

    case getType(toggleAnnotationSelection): {
      const { annotationId } = action.payload;
      const isSelected = state.selectedAnnotations.indexOf(annotationId) >= 0;
      return {
        ...state,
        selectedAnnotations: isSelected
          ? state.selectedAnnotations.filter(id => id !== annotationId)
          : state.selectedAnnotations.concat([annotationId]),
      };
    }

    case getType(selectAnnotationsOfType): {
      const { annotationType } = action.payload;
      const existing = new Set(state.selectedAnnotations);
      const typeIds = Array.from(state.types.entries())
        .filter(([, t]) => t === annotationType)
        .map(([id]) => id);
      const allSelected = typeIds.every(id => existing.has(id));
      if (allSelected && typeIds.length > 0) {
        const removeSet = new Set(typeIds);
        return {
          ...state,
          selectedAnnotations: state.selectedAnnotations.filter(
            id => !removeSet.has(id),
          ),
        };
      }
      const newIds = typeIds
        .filter(id => !existing.has(id))
        .sort((a, b) => {
          const aStart = state.metadata.get(a)?.start ?? Infinity;
          const bStart = state.metadata.get(b)?.start ?? Infinity;
          return aStart - bStart;
        });
      return {
        ...state,
        selectedAnnotations: state.selectedAnnotations.concat(newIds),
      };
    }

    case getType(splitAnnotation): {
      const id = action.payload.annotationId;
      const fullText = appState.annotationPages.fullText;
      const meta = state.metadata.get(id);
      if (!meta) return state;
      const value = state.values.get(id) || '';
      const type = state.types.get(id) || '';

      const segments = computeSplitSegments(meta, value, fullText);
      if (segments.length <= 1) return state;

      // Remove original
      let newState: AnnotationsState = {
        ...state,
        ids: state.ids.filter(i => i !== id),
        metadata: state.metadata.delete(id),
        values: state.values.delete(id),
        types: state.types.delete(id),
        validations: state.validations.delete(id),
      };

      // Add splits
      for (const seg of segments) {
        const newId = uuid();
        const newMeta: AnnotationMetadata = {
          id: newId,
          start: seg.start,
          end: seg.end,
          annotatedBy: meta.annotatedBy,
          annotatedDate: meta.annotatedDate,
          nativeText: seg.nativeText,
        };
        newState = {
          ...newState,
          ids: newState.ids.concat([newId]),
          metadata: newState.metadata.set(newId, newMeta),
          values: newState.values.set(newId, seg.value),
          types: newState.types.set(newId, type),
        };
      }
      return newState;
    }

    case getType(addToChainNesting): {
      const id = action.payload.annotationId;
      if (state.chainNestingOrder.indexOf(id) >= 0) return state;
      const order = state.chainNestingOrder.concat([id]);
      return {
        ...state,
        chainNestingOrder: order,
        selectedAnnotations: order,
      };
    }

    case getType(cancelChainNesting): {
      return {
        ...state,
        chainNestingOrder: [],
        selectedAnnotations: [],
      };
    }

    case getType(fetchAnnotationPages):
      return { ...state, error: null };

    case getType(fetchAnnotationPagesError):
      const { error } = action.payload;
      return { ...state, error, loading: false };

    case getType(saveAnnotations):
      return { ...state, isSaving: true, saveError: null };

    case getType(saveAnnotationsSuccess):
      const isSameSource =
        action.payload.sourceDocumentId === state.sourceDocumentId;
      return {
        ...state,
        isSaving: false,
        lastSaved: isSameSource ? action.payload.time : state.lastSaved,
      };

    case getType(saveAnnotationsError):
      return { ...state, isSaving: false, saveError: action.payload.error };

    case getType(setAnnotationSaveEvent):
      return { ...state, saveEvent: action.payload.saveEvent };

    case getType(setDragTargetId):
      return { ...state, dragTargetId: action.payload.dragTargetId };

    case getType(setDragTargetState):
      return { ...state, dragTargetState: action.payload.dragTargetState };

    case getType(closeDocument):
    case getType(fetchAndSetSourceDocument):
    case getType(setSourceDocument):
      return { ...defaultAnnotationsState, clipboard: state.clipboard };

    default:
      return state;
  }
}

const updateAnnotatedBy = (appState: AppState, state: AnnotationsState) => (
  ids: string[],
) => {
  const activeUser = selectUsername(appState) || '';
  const metadata = state.metadata;
  const metadataUpdates = Map(
    ids.map(id => {
      const prevMetadata = metadata.get(id);
      const sameUser = activeUser === prevMetadata?.annotatedBy;
      if (!prevMetadata || sameUser) return [id, null];
      const newMetadata = { ...prevMetadata, annotatedBy: activeUser };
      return [id, newMetadata];
    }),
  )
    .filter(a => !!a)
    .map(a => a!);
  return metadataUpdates.isEmpty() ? metadata : metadata.merge(metadataUpdates);
};

type SplitSegment = {
  start: number;
  end: number;
  nativeText: string;
  value: string;
};

function splitTextByPattern(
  text: string,
  baseOffset: number,
  pattern: RegExp,
): SplitSegment[] {
  const segments: SplitSegment[] = [];
  let lastEnd = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastEnd) {
      const segText = text.slice(lastEnd, match.index);
      segments.push({
        start: baseOffset + lastEnd,
        end: baseOffset + match.index,
        nativeText: segText,
        value: segText,
      });
    }
    lastEnd = match.index + match[0].length;
  }
  if (lastEnd < text.length) {
    const segText = text.slice(lastEnd);
    segments.push({
      start: baseOffset + lastEnd,
      end: baseOffset + text.length,
      nativeText: segText,
      value: segText,
    });
  }
  return segments;
}

function computeSplitSegments(
  meta: AnnotationMetadata,
  value: string,
  fullText: string,
): SplitSegment[] {
  // 1. Discontinuous: split on segment boundaries, then on newlines
  if (
    meta.discontinuous &&
    meta.discontinuousSegments &&
    meta.discontinuousSegments.length > 1
  ) {
    const segmentSplits = meta.discontinuousSegments
      .filter(
        (s: AnnotationSegment) =>
          typeof s.start === 'number' && typeof s.end === 'number',
      )
      .map((s: AnnotationSegment) => ({
        start: s.start!,
        end: s.end!,
        nativeText: s.nativeText || fullText.slice(s.start!, s.end!),
        value: s.nativeText || fullText.slice(s.start!, s.end!),
      }));
    const result: SplitSegment[] = [];
    for (const seg of segmentSplits) {
      const text = fullText.slice(seg.start, seg.end);
      if (text.indexOf('\n') >= 0) {
        result.push(...splitTextByPattern(text, seg.start, /\n+/g));
      } else {
        result.push(seg);
      }
    }
    return result;
  }

  if (typeof meta.start !== 'number' || typeof meta.end !== 'number') return [];
  const text = fullText.slice(meta.start, meta.end);

  // 2. Contains newlines: split on newlines
  if (text.indexOf('\n') >= 0) {
    return splitTextByPattern(text, meta.start, /\n+/g);
  }

  // 3. Contains commas: split on commas with optional surrounding whitespace
  if (text.indexOf(',') >= 0) {
    return splitTextByPattern(text, meta.start, /\s*,\s*/g);
  }

  // 4. Split on whitespace
  return splitTextByPattern(text, meta.start, /\s+/g);
}
